import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTheme } from "@mui/material/styles";
import { greenButtonSx } from "../../profile/actionStyles";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../components/ui/Toast";
import { useUpsertMyLogEntryMutation } from "../../../api/dailyLogApi";
import { getTimezone, getTimezoneOffset } from "../../../lib/dateUtils";
import Card from "../../../components/ui/Card";
import { goalKey } from "../../goals/goalRules";
import {
  dayGoalsFor,
  dayLogRows,
  goalValueIn,
  savedDayValues,
  visibleDayGoals,
} from "../../../lib/dayGoals";
import GoalRow from "./GoalRow";
import DayCompletionRing from "./DayCompletionRing";

// Değerler ad + biçim anahtarlı (aynı ad iki biçimde ayrı hedef).
function buildInitialValues(goals, dayLog) {
  return savedDayValues(goals, dayLog?.log?.dailyLogs);
}

export default function LogEntryEditor({ localDate, dayLog, onSaved }) {
  const mode = useTheme().palette.mode;
  const { t } = useScriptMode();
  const { user } = useAuth();
  const toast = useToast();

  // Geçmiş gün o günün snapshot hedefleriyle (hedef değeri ve biçimiyle)
  // düzenlenir; bugünkü hedeflerle ezilmez. Snapshot yoksa profil. O gün
  // geçerli olmayan hedef, değeri yoksa gösterilmez ve kaydedilmez.
  const allGoals = useMemo(() => dayGoalsFor(dayLog, user?.goalDetail), [dayLog, user]);
  const goals = useMemo(
    () => visibleDayGoals(allGoals, localDate, buildInitialValues(allGoals, dayLog)),
    [allGoals, localDate, dayLog]
  );
  const [values, setValues] = useState(() => buildInitialValues(goals, dayLog));

  // Gün ya da sunucudaki kayıt değişince formu tazele.
  useEffect(() => {
    setValues(buildInitialValues(goals, dayLog));
  }, [localDate, dayLog, goals]);

  const [upsertLog, { isLoading: savingLog }] = useUpsertMyLogEntryMutation();

  const handleSave = async () => {
    const tz = getTimezone();
    const tzOffset = getTimezoneOffset();
    const dailyLogs = dayLogRows(goals, values);

    const logRes = await upsertLog({
      dailyLogs,
      localDate,
      timezone: tz,
      timezoneOffset: tzOffset,
    });
    if (logRes.error) {
      toast.error(logRes.error.message || t("results_save_failed"));
      return;
    }

    toast.success(t("results_save_success"));
    if (onSaved) onSaved();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 flex flex-col gap-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-SemiBold text-terakkiPrimaryDark">{t("results_my_goals")}</div>
            {/* Hedef ekleme ve düzenleme Hedeflerim sayfasında (takip ayarlarıyla). */}
            <Button component={Link} to="/uygulama/profil" size="small">{t("results_add_edit_goals")}</Button>
          </div>
          {goals.length === 0 ? (
            <p className="text-base text-terakkiNeutral">{t("results_no_goals")}</p>
          ) : (
            goals.map((g) => {
              const key = goalKey(g.label, g.activity);
              return (
                <GoalRow
                  key={key}
                  goal={g}
                  value={goalValueIn(values, g)}
                  onChange={(v) => setValues((prev) => ({ ...prev, [key]: v }))}
                />
              );
            })
          )}
        </Card>
      </div>

      <div className="flex flex-col gap-6">
        <Card>
          <div className="text-base text-terakkiNeutral mb-2 text-center">{t("results_completion_today")}</div>
          <div className="flex justify-center">
            <DayCompletionRing goals={goals} values={values} />
          </div>
        </Card>
        <LoadingButton
          variant="contained"
          loading={savingLog}
          onClick={handleSave}
          fullWidth
          sx={greenButtonSx(mode)}
        >
          {t("results_save")}
        </LoadingButton>
      </div>

    </div>
  );
}
